import { useState, useRef, useEffect } from 'react';
import { useWindowsStore } from '../../store/windowsStore';

/**
 * Terminal — easter egg CLI fake. Digitar comandos, output com typing effect.
 * Comandos:
 *   help, about, skills, projects, contact, whoami, ls, cat, clear, banner,
 *   matrix, grid, lofi, open <app>, close <app>, konami, coffee, sudo, exit
 */
const COMMANDS = {
  help: () => [
    'Available commands:',
    '  help              show this list',
    '  about             bio',
    '  skills            tech stack',
    '  projects          portfolio links',
    '  contact           email + telegram',
    '  whoami            current user',
    '  ls                list files',
    '  cat <file>        show file contents',
    '  banner            system info',
    '  matrix / grid     switch wallpaper mode',
    '  open <app>        open a window (about|projects|skills|...)',
    '  close <app>       close a window',
    '  konami            ???',
    '  coffee            ☕',
    '  sudo <cmd>        run as superuser',
    '  clear             clear screen',
    '  exit              close terminal',
  ],
  about: () => [
    'Pedro Riccio — Blade',
    'Fullstack dev · Salvador/BA · Mil Select Industrial',
    'Stack: React, Next.js, FastAPI, Python, Node, PostgreSQL, Docker.',
  ],
  skills: () => [
    'Lang:      Python 95% · Node 90% · TS 85% · Java',
    'Frontend:  React 90% · Next.js · Tailwind · Vite',
    'Backend:   FastAPI · Express · NestJS · PostgreSQL',
    'Infra:     Docker · VPS Linux · nginx · CI/CD',
    'Security:  Pentest · OWASP · Burp · Nmap',
  ],
  projects: () => [
    '1. milselect       — Next.js + FastAPI + PG (VPS prod)',
    '2. portfolio v2.0  — this desktop OS',
    '3. ip-location     — React + Leaflet + Vite',
    '4. climate-weather — React + Vite + Google Maps',
    '5. blade-console   — orchestrate CLIs via web',
    '6. escritorio-pixel — internal app',
    'github.com/IIBladeII',
  ],
  contact: () => [
    'email:    pedroworkdev@gmail.com',
    'telegram: @BladeGhost',
    'github:   github.com/IIBladeII',
    'site:     pedroriccio.vercel.app',
  ],
  whoami: () => ['blade'],
  ls: () => [
    'about.md  skills.json  projects/',
    'contact.txt  secret.txt  .bash_history',
  ],
  'cat secret.txt': () => [
    '██ SECRET ██',
    'I use Arch BTW.',
  ],
  'cat .bash_history': () => [
    'sudo rm -rf /',
    'git push --force origin main',
    'cd /var/www/html && rm -rf *',
    'cat /etc/shadow',
  ],
  banner: () => [
    '╔════════════════════════════════════════╗',
    '║  Blade OS v2.0 — CYBERPUNK EDITION     ║',
    '║  uptime: see taskbar                   ║',
    '║  cpu: Ryzen 5 5500U · ram: 17GB        ║',
    '║  net: ONLINE · secure boot: ENABLED    ║',
    '╚════════════════════════════════════════╝',
  ],
  coffee: () => [
    '        )  (',
    '       (   ) )',
    '        ) ( (',
    '      _______)_',
    '   .-\'---------|',
    '( C|/\\/\\/\\/\\/\\/|',
    ' \\-/\\/\\/\\/\\/\\/\\-|',
    '  `--------------\\',
    '  `---------------',
    '☕ brewing quantum espresso...',
  ],
  konami: () => [
    '↑↑↓↓←→←→BA',
    '...',
    '🎮 cheats enabled (not really)',
  ],
};

const FAKE_FILES = {
  'about.md': () => COMMANDS.about(),
  'skills.json': () => COMMANDS.skills(),
  'contact.txt': () => COMMANDS.contact(),
};

export default function Terminal() {
  const [lines, setLines] = useState(() => [
    { kind: 'system', text: 'Blade OS terminal v2.0 — type "help" to start' },
  ]);
  const [input, setInput] = useState('');
  const [historyIdx, setHistoryIdx] = useState(-1);
  const inputRef = useRef(null);
  const scrollRef = useRef(null);
  const closeWindow = useWindowsStore((s) => s.closeWindow);
  const openWindow = useWindowsStore((s) => s.openWindow);

  useEffect(() => {
    inputRef.current?.focus();
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [lines]);

  const pushLines = (newLines) => {
    setLines((prev) => [
      ...prev,
      ...newLines.map((text) => ({ kind: 'output', text })),
    ]);
  };

  const pushSystem = (text) => {
    setLines((prev) => [...prev, { kind: 'system', text }]);
  };

  const runCommand = (raw) => {
    const cmd = raw.trim();
    const lower = cmd.toLowerCase();
    setHistoryIdx(-1);

    const echoLine = { kind: 'input', text: `$ ${raw}` };
    setLines((prev) => [...prev, echoLine]);

    if (!cmd) return;

    // cat <file>
    if (lower.startsWith('cat ')) {
      const file = cmd.slice(4).trim();
      const handler = FAKE_FILES[file] || COMMANDS[`cat ${file}`];
      if (handler) return pushLines(handler());
      return pushLines([`cat: ${file}: No such file or directory`]);
    }

    // open / close
    if (lower.startsWith('open ')) {
      const app = cmd.slice(5).trim();
      openWindow(app);
      return pushLines([`opening ${app}...`]);
    }
    if (lower.startsWith('close ')) {
      const app = cmd.slice(6).trim();
      closeWindow(app);
      return pushLines([`closing ${app}...`]);
    }

    // sudo
    if (lower.startsWith('sudo ')) {
      if (lower === 'sudo rm -rf /') {
        return pushLines([
          'Nice try. 😏',
          'Permission denied: user is not in the sudoers file.',
          'This incident will be reported.',
        ]);
      }
      return pushLines(['sudo: permission denied (Blade is root already)']);
    }

    // clear
    if (lower === 'clear') {
      setLines([]);
      return;
    }

    // exit
    if (lower === 'exit') {
      closeWindow('terminal');
      return;
    }

    // wallpaper mode
    if (lower === 'matrix' || lower === 'grid') {
      // dispatch custom event that Taskbar listens to
      window.dispatchEvent(new CustomEvent('terminal:wallpaper', { detail: lower }));
      return pushLines([`wallpaper → ${lower}`]);
    }

    // lofi toggle
    if (lower === 'lofi') {
      openWindow('lofi');
      return pushLines(['opening lofi.fm...']);
    }

    // help + known commands
    if (COMMANDS[lower]) {
      return pushLines(COMMANDS[lower]());
    }

    pushLines([`command not found: ${cmd}`, 'type "help" for available commands']);
  };

  const onKey = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      runCommand(input);
      setInput('');
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      // simplistic history — only previous command
      if (historyIdx === -1) {
        const lastCmd = [...lines].reverse().find((l) => l.kind === 'input');
        if (lastCmd) {
          setInput(lastCmd.text.replace(/^\$\s*/, ''));
          setHistoryIdx(0);
        }
      }
    } else if (e.key === 'l' && e.ctrlKey) {
      e.preventDefault();
      setLines([]);
    }
  };

  return (
    <div
      className="h-full font-mono text-xs flex flex-col bg-[#020008] text-[var(--neon-cyan)] p-3 select-text"
      onClick={() => inputRef.current?.focus()}
    >
      <div ref={scrollRef} className="flex-1 overflow-y-auto pr-2 space-y-0.5">
        {lines.map((line, i) => (
          <div
            key={i}
            className={
              line.kind === 'input'
                ? 'text-[var(--neon-pink)]'
                : line.kind === 'system'
                ? 'text-[var(--text-muted)] italic'
                : 'text-[var(--text-secondary)]'
            }
          >
            {line.text || ' '}
          </div>
        ))}
        <div className="flex items-center gap-1">
          <span className="text-[var(--neon-pink)] shrink-0">$</span>
          <input
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKey}
            className="flex-1 bg-transparent outline-none text-[var(--neon-cyan)] caret-[var(--neon-cyan)]"
            autoFocus
            spellCheck={false}
            autoComplete="off"
            aria-label="Terminal input"
          />
        </div>
      </div>
    </div>
  );
}